// Angular
import { Injectable } from '@angular/core';

// Third party
import _ from 'lodash';
import { Observable, of } from 'rxjs';

// Local library
import { ITicketSlim } from '../+state';

/**
 * U+200B — an invisible character that gives the browser a legal place to wrap
 */
const ZERO_WIDTH_SPACE = '\u200B';

/**
 * Bets tab name: 'saved' | 'active' | 'closed'
 */
export type BetsTab = 'saved' | 'active' | 'closed';

/**
 * Triplet of show...Bets flags (exactly one is true)
 */
export interface BetsTabFlags {
  showSavedBets: boolean;
  showActiveBets: boolean;
  showClosedBets: boolean;
}

/**
 * Submit-saved-bet API response error flags
 */
export interface SubmitSavedBetErrorFlags {
  isExceedsLossLimit: boolean;
  isGenericAdmin: boolean;
}

/**
 * Snapshot for the bets component's three tab lists
 */
export interface IBetsTabSnapshot {
  savedBets$: Observable<ITicketSlim[]>;
  activeBets$: Observable<ITicketSlim[]>;
  closedBets$: Observable<ITicketSlim[]>;
  savedTickets: ITicketSlim[];
  activeTickets: ITicketSlim[];
  closedTickets: ITicketSlim[];
}

/**
 * Race-level bucket in the grouped "My Bets" tree
 */
export interface IBetsRaceGroup {
  raceId: number;
  postTime: string;
  ticketCount: number;
  totalAmount: number;
  tickets: ITicketSlim[];
  /** Stable `eventId-raceId` key for expand/collapse tracking */
  raceKey: string;
}

/**
 * Event-level bucket in the grouped "My Bets" tree
 */
export interface IBetsEventGroup {
  eventId: string;
  eventName: string;
  ticketCount: number;
  totalAmount: number;
  races: IBetsRaceGroup[];
}

/**
 * Top-level grouped bets payload with per-event rows and grand-total
 */
export interface IBetsGrouped {
  events: IBetsEventGroup[];
  grandTotalCount: number;
  grandTotalAmount: number;
}

/**
 * Pure display / formatting helpers for the My Bets component
 */
@Injectable()
export class BetsDisplayService {

  /**
   * Look up event name from the cached all-events list
   * @param {string} eventId Ticket EventId
   * @param {any[]} events Cached all event-track-race list
   * @returns {string} Event name, or 'Not found'
   */
  getEventName(eventId: string, events: any[] | undefined | null): string {
    const item = _.find(
      events,
      (event) => _.get(event, 'ToteResponse.EventDetail.EventId') === eventId
    );
    return _.get(item, 'ToteResponse.EventDetail.EventName', 'Not found');
  }

  /**
   * Sum non-cancelled tickets in the list matching the status
   * @param {ITicketSlim[]} tickets Saved or active tickets
   * @param {number} ticketStatus 1=saved, 3=active, 6=closed
   * @returns {number} Grand total amount
   */
  getTotalAmount(
    tickets: ITicketSlim[] | undefined | null,
    ticketStatus: number
  ): number {
    return _.sumBy(
      _.filter(
        tickets,
        (ticket) => !ticket.IsCancelled && ticket.TicketStatus === ticketStatus
      ),
      'TicketAmount'
    );
  }

  /**
   * Convert modifier name to two-letter abbreviation
   * @param {string} word Modifier name from the ticket
   * @returns {string} Abbreviated label or ''
   */
  convertWordsToAbbre(word: string): string {
    switch (word) {
      case 'Box':
        return 'BX';
      case 'KeyBox':
        return 'KB';
      case 'PowerBox':
        return 'PB';
      case 'KeyWheel':
        return 'KW';
      default:
        return '';
    }
  }

  /**
   * Collapse 3+ consecutive runner numbers in each leg into a range
   * @param {string} runners Slash-separated, comma-delimited runner numbers
   * @returns {string} Same string with consecutive runs replaced by ranges
   */
  formatConsecutiveStringFE(runners: string | undefined | null): string {
    if (!runners) return '';

    const numbersGroups: string[] = runners.split('/');
    const formattedGroups: string[] = [];

    for (const group of numbersGroups) {
      const numbers: string[] = group.split(',');
      let consecutive: string[] = [];
      const formattedGroup: string[] = [];

      for (let i = 0; i < numbers.length; i++) {
        if (parseInt(numbers[i], 10) + 1 === parseInt(numbers[i + 1], 10)) {
          consecutive.push(numbers[i]);
        } else {
          consecutive.push(numbers[i]);

          if (consecutive.length >= 3) {
            formattedGroup.push(
              `${consecutive[0]}-${consecutive[consecutive.length - 1]}`
            );
          } else {
            formattedGroup.push(...consecutive);
          }

          consecutive = [];
        }
      }

      formattedGroups.push(formattedGroup.join(','));
    }

    return formattedGroups.join('/');
  }

  /**
   * Split a getBets$ response into the three tab lists + raw arrays
   * @param {any} response State response from getBets$()
   * @returns {IBetsTabSnapshot} Three observable lists + raw arrays
   */
  parseBetsResponse(response: any): IBetsTabSnapshot {
    const savedTickets: ITicketSlim[] = response?.saved ?? [];
    const activeTickets: ITicketSlim[] = response?.active ?? [];
    const closedTickets: ITicketSlim[] = response?.close ?? [];

    return {
      savedBets$: of(
        savedTickets.filter((ticket) => ticket?.TicketStatus === 1)
      ),
      activeBets$: of(
        activeTickets.filter((ticket) => ticket?.TicketStatus === 3)
      ),
      closedBets$: of(
        closedTickets.filter((ticket) => ticket?.TicketStatus === 6)
      ),
      savedTickets,
      activeTickets,
      closedTickets
    };
  }

  /**
   * Modifier suffix used on bet rows (empty for 'None')
   * @param {string} modifier Ticket Modifier value
   * @returns {string} Two-letter abbreviation, or ''
   */
  modifierAbbreviationFor(modifier: string | undefined | null): string {
    if (!modifier || modifier === 'None') return '';
    return this.convertWordsToAbbre(modifier);
  }

  /**
   * Whether the Pay: $X indicator should show on a closed ticket
   * @param {ITicketSlim} ticket Closed ticket row
   * @returns {boolean} True when NetPayoff exceeds TicketAmount
   */
  hasPositiveNetPayoff(ticket: ITicketSlim): boolean {
    if (!ticket || ticket.IsCancelled) return false;
    const netPayoff = Number(ticket?.NetPayoff ?? 0);
    const ticketAmount = Number(ticket?.TicketAmount ?? 0);
    return netPayoff - ticketAmount > 0;
  }

  /**
   * Probe WageringResponses for the "Exceeds loss limit." error
   * @param {any} wageringResponses WageringResponses array from API
   * @returns {boolean} True when loss-limit error is present
   */
  isExceedsLossLimitError(wageringResponses: any): boolean {
    return _.some(
      wageringResponses,
      { 'Error': 'Exceeds loss limit.' }
    );
  }

  /**
   * Probe WageringResponses for the generic admin error
   * @param {any} wageringResponses WageringResponses array from API
   * @returns {boolean} True when generic error is present
   */
  isGenericAdminError(wageringResponses: any): boolean {
    return _.some(
      wageringResponses,
      (item: any) =>
        item?.Error?.includes?.('Please contact your administrator for more information.')
    );
  }

  /**
   * Look up race PostTime by EventId and RaceId from the cached list
   * @param {string} eventId Ticket EventId
   * @param {number} raceId Ticket RaceId
   * @param {any[]} events Cached all event-track-race list
   * @returns {string} PostTime ISO string, or ''
   */
  getRacePostTime(
    eventId: string,
    raceId: number,
    events: any[] | undefined | null
  ): string {
    const event = _.find(
      events,
      (e) => _.get(e, 'ToteResponse.EventDetail.EventId') === eventId
    );
    const race = _.find(
      _.get(event, 'ToteResponse.EventDetail.Races'),
      (r: any) => r?.RaceId === raceId
    );
    return _.get(race, 'PostTime', '');
  }

  /**
   * Group tickets into a Grand Total → Event → Race → Ticket tree
   * @param {ITicketSlim[]} tickets Flat ticket list for the current tab
   * @param {any[]} events Cached all event-track-race list
   * @returns {IBetsGrouped} Grouped tree with grand-total figures
   */
  groupBets(
    tickets: ITicketSlim[] | undefined | null,
    events: any[] | undefined | null
  ): IBetsGrouped {
    const eventGroupsMap = new Map<string, IBetsEventGroup>();

    for (const ticket of tickets ?? []) {
      if (!ticket) continue;

      const eventId = ticket.EventId ?? '';
      const raceId = ticket.RaceId ?? 0;
      const raceKey = `${eventId}-${raceId}`;

      let eventGroup = eventGroupsMap.get(eventId);
      if (!eventGroup) {
        eventGroup = {
          eventId,
          eventName: this.getEventName(eventId, events),
          ticketCount: 0,
          totalAmount: 0,
          races: []
        };
        eventGroupsMap.set(eventId, eventGroup);
      }

      let raceGroup = eventGroup.races.find((r) => r.raceKey === raceKey);
      if (!raceGroup) {
        raceGroup = {
          raceId,
          postTime: this.getRacePostTime(eventId, raceId, events),
          ticketCount: 0,
          totalAmount: 0,
          tickets: [],
          raceKey
        };
        eventGroup.races.push(raceGroup);
      }

      raceGroup.tickets.push(ticket);

      // Cancelled tickets are visible but excluded from counts/totals.
      if (!ticket.IsCancelled) {
        const ticketAmount = ticket.TicketAmount ?? 0;
        raceGroup.ticketCount += 1;
        raceGroup.totalAmount += ticketAmount;
        eventGroup.ticketCount += 1;
        eventGroup.totalAmount += ticketAmount;
      }
    }

    const eventsList = Array.from(eventGroupsMap.values());
    const grandTotalCount = eventsList.reduce(
      (sum, e) => sum + e.ticketCount,
      0
    );
    const grandTotalAmount = eventsList.reduce(
      (sum, e) => sum + e.totalAmount,
      0
    );

    return {
      events: eventsList,
      grandTotalCount,
      grandTotalAmount
    };
  }

  /**
   * Per-ticket details line (e.g. `$0.20 SPR 1/2/3/4`)
   * @param {ITicketSlim} ticket Ticket to summarise
   * @returns {string} Compact bet details line
   */
  toTicketDetailsLine(ticket: ITicketSlim | undefined | null): string {
    if (!ticket) return '';

    const baseAmount = ticket?.BaseAmount;
    const baseAmountStr =
      baseAmount !== undefined && baseAmount !== null
        ? `$${Number(baseAmount).toFixed(2)}`
        : '';

    const poolId = ticket?.PoolId ?? '';
    const modifierAbbr = this.modifierAbbreviationFor(ticket?.Modifier);
    const runners = this.formatConsecutiveStringFE(ticket?.Runners);

    return [baseAmountStr, poolId, modifierAbbr, runners]
      .filter((segment) => !!segment)
      .join(' ');
  }

  /**
   * Ticket details line that is safe to wrap onto multiple lines. A zero-width
   * space after each leg separator is the only wrap opportunity browsers get
   * inside `1-4/3,7/9`, so a long multi-leg line breaks between legs instead of
   * splitting a two-digit runner number across two lines.
   * @param {ITicketSlim} ticket Ticket to summarise
   * @returns {string} Details line that wraps only at leg boundaries
   */
  toWrappableTicketDetailsLine(ticket: ITicketSlim | undefined | null): string {
    return this.toTicketDetailsLine(ticket).split('/').join(`/${ZERO_WIDTH_SPACE}`);
  }

  // Bets-component orchestration helpers (pure functions)

  /**
   * Resolve a tab name into the show...Bets flag triplet
   * @param {BetsTab} tab Tab to activate
   * @returns {BetsTabFlags} Mutually-exclusive flag triplet
   */
  resolveTabFlags(tab: BetsTab): BetsTabFlags {
    return {
      showSavedBets: tab === 'saved',
      showActiveBets: tab === 'active',
      showClosedBets: tab === 'closed',
    };
  }

  /**
   * Pick the ticket array driving the currently-active tab
   * @param {BetsTab} tab Currently-active tab
   * @param {ITicketSlim[]} savedTickets All saved tickets
   * @param {ITicketSlim[]} activeTickets All active tickets
   * @param {ITicketSlim[]} closedTickets All closed tickets
   * @returns {ITicketSlim[]} Tickets for the active tab
   */
  getCurrentTabTickets(
    tab: BetsTab,
    savedTickets: ITicketSlim[] | undefined | null,
    activeTickets: ITicketSlim[] | undefined | null,
    closedTickets: ITicketSlim[] | undefined | null
  ): ITicketSlim[] {
    if (tab === 'saved') return savedTickets ?? [];
    if (tab === 'closed') return closedTickets ?? [];
    return activeTickets ?? [];
  }

  /**
   * Attach cached geo-location onto a ticket when both coords are valid
   * @param {ITicketSlim} ticket Ticket to enrich
   * @param {{CLat?: number, CLon?: number}} geoLocation Cached coords
   * @returns {ITicketSlim} Ticket with Location attached, or unchanged
   */
  attachGeoLocation(
    ticket: ITicketSlim,
    geoLocation: { CLat?: number; CLon?: number } | undefined | null
  ): ITicketSlim {
    const latitude = geoLocation?.CLat;
    const longitude = geoLocation?.CLon;

    if (!latitude || !longitude) return ticket;

    return {
      ...ticket,
      Location: {
        ...(ticket?.Location ?? {}),
        CLat: latitude,
        CLon: longitude,
      },
    };
  }

  /**
   * True when the ticket's event is Open and race RaceStatus is 0
   * @param {ITicketSlim} ticket Saved ticket being submitted
   * @param {any[]} allEventTrackRace Cached all event-track-race list
   * @returns {boolean} True when the race is open for wagering
   */
  isRaceOpenForSubmit(
    ticket: ITicketSlim | undefined | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    allEventTrackRace: any[] | undefined | null
  ): boolean {
    if (!ticket) return false;

    const currentEvent = _.find(
      allEventTrackRace,
      (event) =>
        _.get(event, 'ToteResponse.EventDetail.EventId') === ticket.EventId
    );

    const currentRace = _.find(
      currentEvent?.ToteResponse?.EventDetail?.Races,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (race: any) => _.get(race, 'RaceId') === ticket.RaceId
    );

    return (
      currentEvent?.ToteResponse?.EventDetail?.EventStatus === 'Open' &&
      currentRace?.RaceStatus === 0
    );
  }

  /**
   * Parse a submit-saved-bet callback into two boolean error flags
   * @param {any} callback Raw isSubmitSavedBetSuccess$ callback
   * @returns {SubmitSavedBetErrorFlags} Pair of independent error booleans
   */
  parseSubmitSavedBetErrors(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: any
  ): SubmitSavedBetErrorFlags {
    const flags: SubmitSavedBetErrorFlags = {
      isExceedsLossLimit: false,
      isGenericAdmin: false,
    };

    const wageringResponses =
      callback?.response?.[0]?.WageringResponses ||
      callback?.response?.WageringResponses;

    if (!Array.isArray(wageringResponses) || wageringResponses.length === 0) {
      return flags;
    }

    flags.isExceedsLossLimit = this.isExceedsLossLimitError(wageringResponses);
    flags.isGenericAdmin = this.isGenericAdminError(wageringResponses);

    return flags;
  }

  /**
   * Extract a user-friendly error message from a cancel-bet callback
   * @param {any} callback Raw isCancelBetSuccess$ callback
   * @returns {string} Non-empty error message for the alert dialog
   */
  parseCancelBetError(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: any
  ): string {
    return callback?.response?.error?.Message || 'Something went wrong';
  }

  /**
   * Build the cancelBetAPI payload with IsCancelled inverted
   * @param {ITicketSlim} ticket Active ticket to toggle
   * @returns {ITicketSlim} Cloned ticket with IsCancelled inverted
   */
  buildCancelTicket(ticket: ITicketSlim): ITicketSlim {
    return {
      ...ticket,
      IsCancelled: !ticket.IsCancelled,
    };
  }
}
